import React from 'react';
import { Checkbox, Radio } from 'antd';
import { FD210 } from '../../../services/pwd210Api';
import { dateToInput, inputToDate } from '../helpers';
import LookupField from '../../../components/LookupField';

interface MasterFieldsProps {
  formData: Partial<FD210>;
  names: Record<string, string>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FD210) => FD210[keyof FD210];
  setField: (key: keyof FD210, value: FD210[keyof FD210]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
}

const E: React.FC<{ error?: string }> = ({ error }) =>
  error ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6 }}>{error}</span> : null;

const Pwd210MasterFields: React.FC<MasterFieldsProps> = React.memo(({
  formData, names, isEditing, isFieldDisabled,
  getField, setField, resolveSingleName, getFieldError,
}) => {
  return (
    <div className="pwd210-tab1">
      {/* === 驗貨方式說明（唯讀） === */}
      <div className="form-section">
        <span className="section-tag">驗貨方式</span>
        <div className="form-grid">
          <label data-contract-field="ui_inspection_method">驗貨方式</label>
          <div>
            <textarea
              readOnly
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
              value={names.inspectionMethod || ''}
              placeholder="(由系統帶入)"
            />
          </div>
        </div>
      </div>

      {/* === 驗貨指示 memo === */}
      <div className="form-section">
        <span className="section-tag">驗貨指示</span>
        <div className="form-grid">
          <label data-contract-field="fd210_40">驗貨指示</label>
          <div>
            <textarea
              rows={5}
              style={{ width: '100%', resize: 'vertical' }}
              disabled={isFieldDisabled('fd210_40')}
              value={getField('fd210_40') as string}
              onChange={e => setField('fd210_40', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* === 廠商 + 出貨日 + 包裝 === */}
      <div className="form-section">
        <span className="section-tag">廠商/包裝</span>
        <div className="form-grid">
          <label data-contract-field="fd210_31">廠商代號{getFieldError && <E error={getFieldError('fd210_31')} />}</label>
          <div className="lookup-field">
            <LookupField
              lookupType="vendor"
              style={{ width: 200 }}
              maxLength={60}
              disabled={isFieldDisabled('fd210_31')}
              value={getField('fd210_31') as string}
              onChange={v => setField('fd210_31', v)}
              onCodeConfirm={code => resolveSingleName('vendor', 'vendor', code)}
            />
            <span className="lookup-name">{names.vendor || '--'}</span>
          </div>

          <label data-contract-field="fd210_32">預計出貨日</label>
          <div>
            <input
              type="date"
              disabled={isFieldDisabled('fd210_32')}
              value={dateToInput(getField('fd210_32') as string | null)}
              onChange={e => setField('fd210_32', inputToDate(e.target.value) as string)}
            />
          </div>

          <label data-contract-field="fd210_33">驗布後包裝代號</label>
          <div className="lookup-field">
            <LookupField
              lookupType="packing"
              style={{ width: 80 }}
              maxLength={2}
              disabled={isFieldDisabled('fd210_33')}
              value={getField('fd210_33') as string}
              onChange={v => setField('fd210_33', v)}
              onCodeConfirm={code => resolveSingleName('packing', 'packing', code)}
            />
            <span className="lookup-name">{names.packing || '--'}</span>
          </div>
        </div>
      </div>

      {/* === Checkboxes (fd210_34 ~ fd210_39) === */}
      <div className="form-section">
        <span className="section-tag">驗布選項</span>
        <div className="checkbox-group">
          <Checkbox
            disabled={isFieldDisabled('fd210_34')}
            checked={!!formData.fd210_34}
            onChange={e => setField('fd210_34', e.target.checked)}
          >
            <span className="field-label" data-contract-field="fd210_34">後段需上膠-需標上上膠面</span>
          </Checkbox>
          <Checkbox
            disabled={isFieldDisabled('fd210_35')}
            checked={!!formData.fd210_35}
            onChange={e => setField('fd210_35', e.target.checked)}
          >
            <span className="field-label" data-contract-field="fd210_35">布匹分正反面</span>
          </Checkbox>
          <Checkbox
            disabled={isFieldDisabled('fd210_36')}
            checked={!!formData.fd210_36}
            onChange={e => setField('fd210_36', e.target.checked)}
          >
            <span className="field-label" data-contract-field="fd210_36">驗布定長為100M-只允許1個接疋</span>
          </Checkbox>
          <Checkbox
            disabled={isFieldDisabled('fd210_37')}
            checked={!!formData.fd210_37}
            onChange={e => setField('fd210_37', e.target.checked)}
          >
            <span className="field-label" data-contract-field="fd210_37">更換白色紙管</span>
          </Checkbox>
          <Checkbox
            disabled={isFieldDisabled('fd210_38')}
            checked={!!formData.fd210_38}
            onChange={e => setField('fd210_38', e.target.checked)}
          >
            <span className="field-label" data-contract-field="fd210_38">打馬克槍</span>
          </Checkbox>
          <Checkbox
            disabled={isFieldDisabled('fd210_39')}
            checked={!!formData.fd210_39}
            onChange={e => setField('fd210_39', e.target.checked)}
          >
            <span className="field-label" data-contract-field="fd210_39">留布頭/布尾</span>
          </Checkbox>
        </div>
      </div>

      {/* === 驗布種類 (optiongroup → Radio.Group) === */}
      <div className="form-section">
        <span className="section-tag">驗布種類{getFieldError && <E error={getFieldError('fd210_14')} />}</span>
        <div className="form-grid">
          <label data-contract-field="fd210_14">驗布種類</label>
          <div>
            <Radio.Group
              disabled={isFieldDisabled('fd210_14')}
              value={getField('fd210_14') as string}
              onChange={e => setField('fd210_14', e.target.value)}
            >
              <Radio value="1">成品</Radio>
              <Radio value="2">半成品</Radio>
            </Radio.Group>
          </div>
        </div>
      </div>
    </div>
  );
});

Pwd210MasterFields.displayName = 'Pwd210MasterFields';

export default Pwd210MasterFields;
